/**
 * Schedule coverage: does every assessment the schedule asks for have a CRF
 * form? (docgen crf-list 1b, 2026-10-02)
 *
 * The CRF list comes from one free-form model call. On an invented study that
 * call skipped the Haematology form although the schedule lists "Haematology
 * (local laboratory)" at all eight visits, and nothing afterwards noticed. This
 * module is the deterministic safety net around that call:
 *
 *   1. collectAssessments: what the schedule asks for (studyDesign visits and
 *      the protocol analysis schedule and lab tests).
 *   2. formBearingUnits: which of those capture data on a form (a lab panel,
 *      vital signs, an exam, an ECG, a questionnaire, a pregnancy test, the
 *      standard history and safety forms) and which do not (dosing, dispensing,
 *      "instruct the participant", reminders). Unknown wording is NOT
 *      form-bearing: a guess must never block an approval.
 *   3. uncoveredUnits: which form-bearing units no form covers, judged by the
 *      ONE tolerant unique-only matcher (schedule/visitFormMatch.ts). Two forms
 *      qualifying counts as covered: a form exists, a person picks.
 *   4. addCoverageForms: one form per uncovered unit, marked as added by the
 *      coverage rule with the assessment it came from.
 *
 * Pure. No Firestore, no model. Shared by the suggest callable, the B6 check
 * and the CRF Builder's approval gate.
 */
import { dosingGuard } from "./visitFormWording";
import { singleSexRestriction } from "../crf/studySex";
import { matchVisitForms, formsWithAliases, type CrfFormRef, type FormAliases } from "./visitFormMatch";

export type CoverageCategory = "screening" | "demographics" | "medical_history" | "procedures" | "outcomes" | "safety" | "administrative" | "other";

export type AssessmentSource = "schedule" | "protocol" | "labTest";

export interface ScheduledAssessment {
  label: string;
  source: AssessmentSource;
  /** Visit name, when the source is a visit; empty for the study-wide lab list. */
  visit: string;
}

/** One form the schedule needs: every assessment that points at it, grouped. */
export interface CoverageUnit {
  /** Normalised key (spelling-folded), the grouping identity. */
  key: string;
  /** Plain name for the form this unit needs ("Haematology"). */
  formName: string;
  category: CoverageCategory;
  /** The assessment labels that led here, first spelling kept, de-duplicated. */
  labels: string[];
  /** Visits the unit is scheduled at, de-duplicated. */
  visits: string[];
  /** True for a laboratory panel (Haematology, Clinical Chemistry, Urinalysis, Coagulation, Lipid Panel). */
  labPanel: boolean;
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}
function nameOf(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (v && typeof v === "object") return str((v as any).name) || str((v as any).label) || str((v as any).test);
  return "";
}

function keyOf(s: string): string {
  return fold(s).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

/**
 * Wording that is an instruction, a reminder or a contact, never data capture.
 * A dose WORD is not here: "Pre-dose ECG" is an ECG. Doses and frequencies
 * ("10 mg BID") are caught by dosingGuard.
 */
const NOT_A_FORM =
  /\b(instruct|remind|reminder|dispens|administ|dosing instruction|take home|return (?:of )?(?:unused )?(?:study )?(?:drug|medication)|drug supply|training|counsel|telephone|phone|call|contact|compliance)/i;

/** Body measures that are vital-signs content: covered by Vital Signs or Demographics, never a form of their own. */
const BODY_MEASURE = /\b(bmi|body mass index|height|weight)\b/i;

export type { AliasEntry } from "./scheduleAliases";
export { COVERAGE_ALIASES, canonicalIds } from "./scheduleAliases";
import { COVERAGE_ALIASES, canonicalIds, fold } from "./scheduleAliases";

const QUESTIONNAIRE =
  /\bquestionnaire\b|\bscale\b|\binventory\b|\bpatient[- ]reported\b|\bpro\b|\bvas\b|\bnrs\b|\bc[- ]?ssrs\b|\beq[- ]?5d\b|\bsf[- ]?36\b|\bdlqi\b|\bquality of life\b|\blife quality index\b/i;

/** Lab analytes, by panel. A protocol's lab list names analytes, not panels. */
const ANALYTE_PANELS: Array<{ re: RegExp; panel: string }> = [
  { re: /\b(h(a)?emoglobin|h(a)?ematocrit|platelet|wbc|white blood|red blood|rbc|neutrophil|lymphocyte|monocyte|eosinophil|basophil|reticulocyte)/i, panel: "Haematology" },
  { re: /\b(alt|ast|alanine|aspartate|bilirubin|creatinine|urea|bun|sodium|potassium|glucose|albumin|alkaline phosphatase|calcium|chloride|bicarbonate|ggt|gamma)\b/i, panel: "Clinical Chemistry" },
];

const LAB_PANEL_NAMES = new Set(["haematology", "hematology", "clinical chemistry", "urinalysis", "coagulation", "lipid panel"]);

/**
 * A form that is one combined laboratory form ("Laboratory Results",
 * "Laboratory Tests", "Safety Laboratory Assessments"): every word is a laboratory
 * word. Measured on the reference study, which captures all its blood and urine
 * tests on one "Laboratory Results" form: judging each panel separately would
 * refuse a study whose lab data is fully captured. It covers lab panels only.
 */
const LAB_WORDS = new Set(["laboratory", "lab", "labs", "result", "results", "test", "tests", "assessment", "assessments", "panel", "panels", "data", "local", "central", "safety", "clinical", "sample", "samples", "collection"]);
export function isCombinedLabForm(name: string): boolean {
  const ws = keyOf(name).split(" ").filter(Boolean);
  return ws.length > 0 && ws.some((w) => w === "laboratory" || w === "lab" || w === "labs") && ws.every((w) => LAB_WORDS.has(w));
}

export const RANDOMISATION_KEY = "randomisation";

function classify(label: string): { formName: string; category: CoverageCategory } | null {
  if (!label) return null;
  if (dosingGuard(label).blocked) return null;
  if (NOT_A_FORM.test(label)) return null;
  if (BODY_MEASURE.test(label)) return null;
  const id = canonicalIds(label)[0];
  // Randomisation is needed because the STUDY is randomised (randomisationUnit), never because a schedule line names it.
  if (id === "Randomisation") return null;
  if (id) {
    const e = COVERAGE_ALIASES.find((x) => x.id === id)!;
    const name = id === "Haematology" ? (/haem/i.test(label) ? "Haematology" : "Hematology") : id;
    return { formName: name, category: e.category };
  }
  if (QUESTIONNAIRE.test(label)) {
    return { formName: label.replace(/\([^)]*\)/g, " ").replace(/\s+/g, " ").trim() || label, category: "outcomes" };
  }
  return null;
}

/** True when the label is the kind that captures data on a form. */
export function isFormBearing(label: string): boolean {
  return classify(str(label)) !== null;
}

/**
 * What the schedule asks for. Reads studyDesign/config visits (assessments,
 * else the labels still waiting in formsToConfirm), the protocol analysis
 * schedule (visits[].procedures) and its labTests. Raw labels as read.
 */
export function collectAssessments(studyDesign: any, analysis: any): ScheduledAssessment[] {
  const out: ScheduledAssessment[] = [];
  const visits: any[] = Array.isArray(studyDesign?.visits) ? studyDesign.visits : [];
  for (const v of visits) {
    const visit = str(v?.name) || str(v?.id);
    const own: unknown[] = Array.isArray(v?.assessments) && v.assessments.length
      ? v.assessments
      : Array.isArray(v?.formsToConfirm) ? v.formsToConfirm : [];
    for (const a of own) {
      const label = nameOf(a);
      if (label) out.push({ label, source: "schedule", visit });
    }
  }
  const pvisits: any[] = Array.isArray(analysis?.schedule?.visits) ? analysis.schedule.visits : [];
  for (const v of pvisits) {
    const visit = str(v?.name) || str(v?.id);
    for (const key of ["procedures", "assessments"] as const) {
      for (const a of Array.isArray(v?.[key]) ? v[key] : []) {
        const label = nameOf(a);
        if (label) out.push({ label, source: "protocol", visit });
      }
    }
  }
  for (const t of Array.isArray(analysis?.labTests) ? analysis.labTests : []) {
    const label = nameOf(t);
    if (label) out.push({ label, source: "labTest", visit: "" });
  }
  // A study that enrols males only has no pregnancy test for the subject (a partner's pregnancy is a different form).
  if (singleSexRestriction(analysis) === "male") {
    return out.filter((a) => !(canonicalIds(a.label)[0] === "Pregnancy Test" && !/partner|spous/i.test(a.label)));
  }
  return out;
}

/** The form-bearing needs of the schedule, grouped one per form. */
export function formBearingUnits(assessments: ScheduledAssessment[]): CoverageUnit[] {
  const units = new Map<string, CoverageUnit>();
  for (const a of assessments ?? []) {
    let hit = classify(a.label);
    // A lab list names analytes ("Hemoglobin", "ALT"): they point at a panel.
    if (!hit && a.source === "labTest") {
      const p = ANALYTE_PANELS.find((x) => x.re.test(a.label));
      if (p) hit = { formName: p.panel, category: "safety" };
    }
    if (!hit) continue;
    const key = keyOf(hit.formName);
    // A randomisation form is needed because the STUDY is randomised, never because a schedule line names it.
    if (key === RANDOMISATION_KEY) continue;
    let u = units.get(key);
    if (!u) {
      u = { key, formName: hit.formName, category: hit.category, labels: [], visits: [], labPanel: LAB_PANEL_NAMES.has(key) };
      units.set(key, u);
    }
    if (!u.labels.some((l) => keyOf(l) === keyOf(a.label))) u.labels.push(a.label);
    if (a.visit && !u.visits.includes(a.visit)) u.visits.push(a.visit);
  }
  return [...units.values()];
}

export interface CoverageForm {
  name: string;
  code?: string | null;
}

/** Does any form cover this unit? The tolerant unique-only matcher decides; two qualifying forms still count as covered. */
export function isUnitCovered(unit: CoverageUnit, forms: CoverageForm[], aliases?: FormAliases | null): boolean {
  const refs: CrfFormRef[] = formsWithAliases(
    (forms ?? []).filter((f) => str(f?.name)).map((f) => ({ name: fold(str(f.name)), code: f.code ?? null })),
    aliases,
  );
  if (refs.length === 0) return false;
  if (unit.labPanel && refs.some((r) => isCombinedLabForm(r.name))) return true;
  // The alias table, read through the FORM name: "CBC" or "Full Blood Count" covers Haematology.
  const want = canonicalIds(unit.formName)[0];
  if (want && refs.some((r) => canonicalIds(r.name).includes(want))) return true;
  const candidates = [unit.formName, ...unit.labels].map(fold);
  for (const c of candidates) {
    const r = matchVisitForms([c], refs);
    if (r.matched.length > 0 || r.ambiguous.length > 0) return true;
  }
  // Head phrase, outside the alias families: "Skin Assessment" covers "Skin assessment at the patch site (...)".
  if (!want && candidates.some((c) => refs.some((r) => isHeadPhraseOf(r.name, c)))) return true;
  return false;
}

// Place words only. "of", "in", "with", "for" often name a different instrument ("Clinical Global Impression of Severity").
const HEAD_PREPOSITIONS = new Set(["at", "on"]);
const HEAD_GENERIC = new Set(["assessment", "assessments", "form", "record", "data", "study", "visit", "test", "tests", "evaluation"]);

/** The form's name is the start of the label, and the label goes on only with a preposition ("at the patch site"). */
function isHeadPhraseOf(formName: string, label: string): boolean {
  const f = keyOf(formName).split(" ").filter(Boolean);
  const l = keyOf(label.replace(/\([^)]*\)/g, " ")).split(" ").filter(Boolean);
  if (f.length === 0 || f.every((w) => HEAD_GENERIC.has(w))) return false;
  if (l.length <= f.length || !f.every((w, i) => l[i] === w)) return false;
  return HEAD_PREPOSITIONS.has(l[f.length]);
}

export function uncoveredUnits(units: CoverageUnit[], forms: CoverageForm[], aliases?: FormAliases | null): CoverageUnit[] {
  return (units ?? []).filter((u) => !isUnitCovered(u, forms, aliases));
}

export interface CoverageAddedForm {
  name: string;
  rationale: string;
  category: CoverageCategory;
  addedByCoverageRule: true;
  /** The schedule assessment the form came from. */
  fromAssessment: string;
  /** Short note on what the form should capture, for the variable step. */
  contentHint?: string;
}

export interface RandomisationNeed {
  required: boolean;
  blinded: boolean;
  strata: string[];
}

function strList(v: unknown): string[] {
  return (Array.isArray(v) ? v : []).map(nameOf).filter(Boolean);
}

/**
 * Is the study randomised, is it blinded, and by what is it stratified?
 * Study Settings win ("yes"/"no"); otherwise the protocol analysis decides.
 * Stratification factors are optional: absent is tolerated.
 */
export function randomisationNeed(studySetup: any, analysis: any): RandomisationNeed {
  const sd = analysis?.studyDesign;
  const rz = sd?.randomization;
  const rzText = str(rz);
  const fromAnalysis = rz === true || (/random/i.test(rzText) && !/\b(non|not)[\s-]*random/i.test(rzText));
  const setting = studySetup?.randomized === true ? "yes" : studySetup?.randomized === false ? "no" : str(studySetup?.randomized).toLowerCase();
  const required = setting === "yes" ? true : setting === "no" ? false : fromAnalysis;
  const b = str(studySetup?.blinding).toLowerCase();
  const ab = str(sd?.blinding);
  const blinded = ["single_blind", "double_blind", "triple_blind"].includes(b) ||
    (b !== "open_label" && /blind|mask/i.test(ab) && !/open|\bnone\b|not (?:blind|mask)/i.test(ab));
  const strata = [...new Set([...strList(analysis?.stratificationFactors), ...strList(sd?.stratificationFactors), ...strList(analysis?.randomization?.stratificationFactors)])];
  return { required, blinded, strata };
}

/** The unit B6 and the coverage rule judge for a randomised study; null when not randomised. */
export function randomisationUnit(need: RandomisationNeed): CoverageUnit | null {
  if (!need.required) return null;
  return { key: RANDOMISATION_KEY, formName: "Randomisation", category: "administrative", labels: ["Randomisation"], visits: [], labPanel: false };
}

export function randomisationFormFor(need: RandomisationNeed): CoverageAddedForm {
  const parts = ["randomisation date", "randomisation or kit number from the IRT"];
  if (need.strata.length) parts.push(`stratification factors (${need.strata.join("; ")})`);
  if (need.blinded) parts.push("emergency unblinding record (date, reason, who)");
  return {
    name: "Randomisation",
    rationale: "Added by the coverage rule: the study is randomised and no form records the randomisation.",
    category: "administrative",
    addedByCoverageRule: true,
    fromAssessment: "the study is randomised",
    contentHint: `Capture: ${parts.join(", ")}.`,
  };
}

function visitsPhrase(visits: string[]): string {
  if (visits.length === 0) return "in the protocol";
  if (visits.length === 1) return `at ${visits[0]}`;
  return `at ${visits.length} visits`;
}

/** One form per uncovered unit. Returns the forms to append; the caller keeps its own list. */
export function coverageFormsFor(uncovered: CoverageUnit[]): CoverageAddedForm[] {
  return (uncovered ?? []).map((u) => ({
    name: u.formName,
    rationale: `Added by the schedule coverage rule: the protocol schedules "${u.labels[0]}" ${visitsPhrase(u.visits)} and no form covered it.`,
    category: u.category,
    addedByCoverageRule: true as const,
    fromAssessment: u.labels[0],
  }));
}

/** Plain-words statement for a screen or a toast. */
export function uncoveredSentence(uncovered: CoverageUnit[]): string {
  const names = uncovered.map((u) => `${u.formName} (scheduled as "${u.labels[0]}")`);
  return `The schedule needs a form for ${names.join("; ")}.`;
}

export interface ApprovalGateResult {
  ok: boolean;
  uncovered: CoverageUnit[];
  /** Plain words for a toast; empty when ok. */
  message: string;
}

/**
 * The CRF Builder's approval gate. Approve and Approve All refuse while a
 * form-bearing scheduled assessment has no form in the builder's list at all
 * (any status): a form still in draft can be approved in the same step, a form
 * that does not exist cannot. Check B6 is the stricter later view and also
 * reports a form that exists but is not approved yet.
 */
export function approvalCoverageGate(args: {
  studyDesign: any;
  analysis?: any;
  forms: CoverageForm[];
}): ApprovalGateResult {
  const units = formBearingUnits(collectAssessments(args.studyDesign, args.analysis));
  const uncovered = uncoveredUnits(units, args.forms, args.studyDesign?.formAliases);
  if (uncovered.length === 0) return { ok: true, uncovered: [], message: "" };
  return {
    ok: false,
    uncovered,
    message: `Cannot approve yet. ${uncoveredSentence(uncovered)} Add the missing form to the list, then approve.`,
  };
}
